import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

/**
 * ETAPA VISUAL 2 — CABEÇALHO DE PÁGINA PADRÃO
 *
 * Antes: cada tela abria com `<h1>` + `<p>` + botões disputando a MESMA faixa
 * horizontal, com o mesmo peso. Não havia "onde estou" (nenhuma tela tinha
 * contexto/trilha) e as ações ficavam misturadas ao título.
 *
 * Agora, três faixas com papéis distintos:
 *   1. TRILHA  — cinza pequeno, diz onde a tela está ("Campeonatos / Copa X").
 *   2. TÍTULO  — medalhão dourado + `h1` + descrição (o ponto de entrada).
 *   3. AÇÕES   — alinhadas à direita, separadas do título.
 *
 * É só composição: nenhuma tela mudou de dado, rota, permissão ou regra.
 *
 * Props:
 *   contexto  — string OU lista de passos. Cada passo pode ser
 *               `{ rotulo, to }` (vira link) ou só `rotulo`.
 *   titulo    — texto do `h1` (obrigatório).
 *   descricao — linha de apoio abaixo do título (opcional).
 *   icone     — componente de ícone (lucide) que vai dentro do medalhão.
 *   acoes     — nós (botões/links) alinhados à direita.
 *   semMedalhao — desliga o medalhão (telas onde o título já tem ícone forte).
 */
export function CabecalhoPagina({
  contexto,
  titulo,
  descricao,
  icone: Icone,
  acoes,
  semMedalhao = false,
  className = '',
}) {
  const passos = (Array.isArray(contexto) ? contexto : contexto ? [contexto] : [])
    .map((passo) => (typeof passo === 'string' ? { rotulo: passo } : passo))
    .filter((passo) => passo && passo.rotulo);

  return (
    <header
      className={`flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between ${className}`}
      data-testid="cabecalho-pagina"
    >
      <div className="min-w-0">
        {passos.length > 0 && (
          <nav
            aria-label="Você está em"
            className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-[#71717A]"
            data-testid="cabecalho-trilha"
          >
            {passos.map((passo, indice) => (
              <span key={`${passo.rotulo}-${indice}`} className="flex items-center gap-1.5">
                {indice > 0 && <ChevronRight size={12} aria-hidden="true" className="opacity-60" />}
                {passo.to ? (
                  <Link to={passo.to} className="transition-colors hover:text-[#D4AF37]">
                    {passo.rotulo}
                  </Link>
                ) : (
                  <span>{passo.rotulo}</span>
                )}
              </span>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-3">
          {!semMedalhao && Icone && (
            <span className="medalhao" aria-hidden="true" data-testid="cabecalho-medalhao">
              <Icone size={20} />
            </span>
          )}
          <div className="min-w-0">
            {/* ETAPA 8 (densidade) — `text-2xl` (24px) por padrão; volta a
                `text-3xl` a partir de `2xl`. O título de 30px disputava peso
                com os dados numa janela de notebook. */}
            <h1 className="text-2xl 2xl:text-3xl font-bold text-white font-['Chivo']">{titulo}</h1>
            {descricao && <p className="text-[#A1A1AA] mt-1">{descricao}</p>}
          </div>
        </div>
      </div>

      {acoes && (
        <div
          className="flex flex-wrap items-center gap-2 lg:shrink-0 lg:justify-end"
          data-testid="cabecalho-acoes"
        >
          {acoes}
        </div>
      )}
    </header>
  );
}

/**
 * ETAPA VISUAL 2 — FAIXA DE FILTROS
 *
 * Os filtros deixam de ficar soltos na mesma linha do título (competindo com
 * ele) e passam a viver numa faixa própria, com micro-rótulo. O conteúdo é
 * exatamente o mesmo que a tela já renderizava — só o continente mudou.
 */
export function FaixaFiltros({ rotulo = 'Filtros', children, className = '' }) {
  return (
    <section
      className={`faixa-filtros rounded-xl border border-[#27272A] bg-[#141414] p-4 ${className}`}
      data-testid="faixa-filtros"
      aria-label={rotulo}
    >
      {rotulo && <p className="micro-rotulo mb-3">{rotulo}</p>}
      {children}
    </section>
  );
}
